/** @file gizmoFor: the gizmo model and on-screen size for the current selection, tool, space and pivot. */
import type { EditorState, Tool } from '../../state/editorTypes';
import { cssToTarget } from './cssToTarget';
import type { GizmoModel } from './gizmoModel';
import { gizmoModel } from './gizmoModel';
import { gizmoRadius } from './gizmoRadius';
import { jointFracsOf } from './jointFracsOf';
import type { V3 } from './matrixRows';
import { pictureWorldPoint } from './pictureWorldPoint';
import { poseNode } from './poseNode';
import type { PreviewPuppet } from './previewPuppet';
import type { ViewportCtx } from './viewportCtx';

/**
 * Where the gizmo is and how big: `size` in world units (`gizmoRadius` CSS pixels for the tool and the viewport's CSS
 * height, so the handles keep their screen size at any zoom) and `pxPerCss`, target pixels per CSS pixel.
 */
export type GizmoPlacement = { tool: Tool; model: GizmoModel; size: number; pxPerCss: number };

/** The viewport's CSS height (the layout's device height / dpr), else the container's. */
function viewHeight(ctx: ViewportCtx): number {
  const l = ctx.camera?.layout() ?? null;
  return l === null ? ctx.container.clientHeight : l.device[1] / l.fit.dpr;
}

/**
 * The model with its origin where the picture shows that joint (`pictureWorldPoint`, the bone marks' rule): the
 * fractions of the node it sits on (the active node, or its parent at a bone's base); a selection's centre takes the
 * fallback of a node that draws nothing.
 */
function onPicture(ctx: ViewportCtx, p: PreviewPuppet, s: EditorState, model: GizmoModel): GizmoModel {
  if (ctx.scene === null || ctx.profile === null) return model;
  const node = poseNode(p.instance.state().pose, model.active);
  const at = s.tool === 'rotate' && node.parent >= 0 ? node.parent : model.active;
  const j = s.pivot === 'pivot' ? at : s.selection.nodes.length === 1 ? model.active : -1;
  const fracs = jointFracsOf(p, ctx.profile);
  const fallback = ctx.profile.halfPixel ? 0.5 : 0;
  const out: V3 = [0, 0, 0];
  pictureWorldPoint(out, model.origin, ctx.scene.camera.view(), fracs[2 * j] ?? fallback, fracs[2 * j + 1] ?? fallback);
  return { ...model, origin: out };
}

/** Null in turntable mode, with nothing selected, or before the puppet is drawn. */
export function gizmoFor(ctx: ViewportCtx): GizmoPlacement | null {
  const p = ctx.puppet();
  if (ctx.mode !== 'edit' || p === null || ctx.scene === null) return null;
  const s = ctx.store.getState();
  const indices = s.selection.nodes.map((n) => p.names.indexOf(n)).filter((i) => i >= 0);
  // the rotate tool turns the bone about its base, like the original's editor: its gizmo sits at the base joint
  const model = gizmoModel(indices, p.instance.state().pose, s.space, s.pivot, [0, 0, 0], s.tool === 'rotate' ? 'base' : 'node');
  if (model === null) return null;
  const pxPerCss = cssToTarget(ctx);
  return { tool: s.tool, model: onPicture(ctx, p, s, model), size: (gizmoRadius(s.tool, viewHeight(ctx)) * pxPerCss) / ctx.scene.camera.view().pixelsPerUnit, pxPerCss };
}
